import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { container } from '@/container/CompositionRoot';
import { GlobalFilterValueService, type FilterValueOption } from '@/features/global-filters/GlobalFilterValueService';
import { providerIconForType } from '@/shared/ProviderIconComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import type { AnalyticsField } from '@/shared/analytics/AnalyticsTypes';
import { filterValueLabel } from '@/shared/analytics/MetricFormatting';
import { FilterValuesViewModel } from './FilterValuesViewModel';
import FilterValueChecklist from './FilterValueChecklist';

const USER_SEARCH_DEBOUNCE_MS = 300;
const SEARCHABLE_THRESHOLD = 8;

interface FieldEditorProps {
    selected: string[];
    onToggle: (value: string, checked: boolean) => void;
}

/** Checkbox list of every provider connection (with platform icons), applied on toggle. */
export function ProviderConnectionChecklist({ selected, onToggle }: FieldEditorProps): ReactElement {
    const service = useMemo(() => container.resolve(GlobalFilterValueService), []);
    const [options, setOptions] = useState<FilterValueOption[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect((): (() => void) => {
        let cancelled = false;
        void service
            .loadProviders()
            .then((loaded) => {
                if (!cancelled) setOptions(loaded);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return (): void => {
            cancelled = true;
        };
    }, [service]);

    const data = options.map((option) => ({
        value: option.value,
        label: option.label,
        icon: option.providerTypeId !== undefined ? providerIconForType(option.providerTypeId, 16) : null,
    }));

    return (
        <FilterValueChecklist
            options={data}
            selected={selected}
            onToggle={onToggle}
            loading={loading}
            searchable={options.length >= SEARCHABLE_THRESHOLD}
            searchPlaceholder="Search connections"
            emptyMessage="No connections"
            labelFor={(value) => service.providerLabel(value)}
        />
    );
}

/** Debounced directory search over people; selected people stay pinned so they can be unticked. */
export function PersonSearchChecklist({ selected, onToggle }: FieldEditorProps): ReactElement {
    const service = useMemo(() => container.resolve(GlobalFilterValueService), []);
    const [options, setOptions] = useState<FilterValueOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');

    useEffect((): (() => void) | undefined => {
        const trimmed = query.trim();
        if (trimmed.length === 0) {
            setOptions([]);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        const handle = setTimeout(() => {
            void service
                .searchUsers(trimmed)
                .then((loaded) => {
                    if (!cancelled) setOptions(loaded);
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });
        }, USER_SEARCH_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [query, service]);

    return (
        <FilterValueChecklist
            options={options}
            selected={selected}
            onToggle={onToggle}
            loading={loading}
            onSearch={setQuery}
            searchPlaceholder="Search people by name or email"
            emptyMessage={query.trim().length === 0 ? 'Type to search' : 'No matches'}
        />
    );
}

/** Top values (most-active first) of a page-scoped field, searchable, with a free-text escape hatch. */
export function LocalFieldChecklist({ field, selected, onToggle }: FieldEditorProps & { field: AnalyticsField }): ReactElement {
    const valuesVm = useViewModel(FilterValuesViewModel);

    useEffect(() => {
        void valuesVm.loadFor(field);
    }, [valuesVm, field]);

    const options = valuesVm.field === field ? valuesVm.options.map((value) => ({ value, label: filterValueLabel(field, value) })) : [];

    return (
        <FilterValueChecklist
            options={options}
            selected={selected}
            onToggle={onToggle}
            loading={valuesVm.loading}
            searchable
            allowFreeValue
            searchPlaceholder="Search values"
            emptyMessage="No values yet"
            labelFor={(value) => filterValueLabel(field, value)}
        />
    );
}
